import React, { useEffect, useMemo } from 'react';

import { preloadImages, useImagesReady } from '@/shared/hooks/useImagesReady';
import '@/shared/styles/ArtworkReveal.css';

import { WaitlistLogo } from '../../waiting-list/components/WaitlistLayout';
import '../styles/onboarding.css';

/**
 * Right-hand artwork per onboarding step (Figma MVP-DESIGNES 9224:4381,
 * 9224:4435, 9224:4431, 9226:4449): a gradient backdrop (PNG @2x, exported as
 * it is clipped by the frame) covering the pane, with an SVG illustration
 * centred on top (see `onboarding.css`).
 */
export const ONBOARDING_ARTWORK = {
    about: {
        gradient: '/images/onboarding/gradient-about.png',
        illustration: '/images/onboarding/illustration-about.svg',
    },
    workspace: {
        gradient: '/images/onboarding/gradient-workspace.png',
        illustration: '/images/onboarding/illustration-workspace.svg',
    },
    improve: {
        gradient: '/images/onboarding/gradient-improve.png',
        illustration: '/images/onboarding/illustration-improve.svg',
    },
    audience: {
        gradient: '/images/onboarding/gradient-audience.png',
        illustration: '/images/onboarding/illustration-audience.svg',
    },
};

/**
 * Every step's gradient and illustration.  The pane only waits for the
 * current step's pair; the rest (each gradient is ~5 MB) are warmed in the
 * cache once that pair is on screen, so moving to a later step swaps in
 * artwork that is already there instead of popping in a gradient and, later,
 * its illustration.
 */
export const ONBOARDING_ARTWORK_SOURCES = Object.values(ONBOARDING_ARTWORK).flatMap(({ gradient, illustration }) => [
    gradient,
    illustration,
]);

/**
 * Page chrome for the creator onboarding wizard (/create-workspace).
 *
 * `variant="split"` (default) is the form column (logo, centred 501px white
 * card, action row under the card) next to a full-height gradient panel with
 * a centred illustration, each taking half the page; `artwork` picks the
 * artwork for the current step.  Both layers of the pane are faded in
 * together once decoded (`useImagesReady`), and the other steps' artwork is
 * warmed in the cache in the background.
 * `variant="wide"` (terms step, Figma 9236:2369) drops the panel and widens the
 * card to 760px.
 *
 * `footer` renders in the right-aligned action row directly under the card
 * (the "Next" / "Continue" primary button); `progress` is the step indicator
 * pinned to the bottom of the column (Figma 9251:5312), below the centred
 * card + action block.  `cardClassName` lets a step tune the card (the merged
 * goals card uses a tighter header gap, Figma 9224:4431).
 */
export default function OnboardingLayout({
    variant = 'split',
    artwork = 'about',
    footer = null,
    progress = null,
    cardClassName = '',
    children,
}) {
    const art = ONBOARDING_ARTWORK[artwork] || ONBOARDING_ARTWORK.about;
    const sources = useMemo(() => [art.gradient, art.illustration], [art]);
    const artworkReady = useImagesReady(variant === 'split' ? sources : []);
    useEffect(() => {
        if (artworkReady) preloadImages(ONBOARDING_ARTWORK_SOURCES.filter((src) => !sources.includes(src)));
    }, [artworkReady, sources]);
    const cardClass = `onboarding-card fs-unmask${cardClassName ? ` ${cardClassName}` : ''}`;
    return (
        <div className={`onboarding-page onboarding-page-${variant}`}>
            <div className="onboarding-column">
                <WaitlistLogo size="small" />
                <div className="onboarding-content">
                    <div className="onboarding-main">
                        <main className={cardClass}>{children}</main>
                        {footer ? <div className="onboarding-actions">{footer}</div> : null}
                    </div>
                    {progress}
                </div>
            </div>
            {variant === 'split' ? (
                <div
                    className={`onboarding-art onboarding-art-${artwork} artwork-reveal${artworkReady ? ' is-ready' : ''}`}
                    aria-hidden="true"
                >
                    <img className="onboarding-art-gradient" src={art.gradient} alt="" fetchpriority="high" decoding="async" />
                    <img className="onboarding-art-illustration" src={art.illustration} alt="" fetchpriority="high" decoding="async" />
                </div>
            ) : null}
        </div>
    );
}
